/**
 * Fixes up raw Basecamp comment/description HTML before it is handed to
 * `dangerouslySetInnerHTML`.
 *
 * Root cause this addresses: Basecamp's rich-text `content` represents an
 * @mention as `<bc-attachment content-type="application/vnd.basecamp.mention">`
 * wrapping a `<figure><img class="avatar">...<figcaption>Name</figcaption></figure>`.
 * When a mention appears mid-sentence, Basecamp emits it *inside* a `<p>` (e.g.
 * `<p>Hi <bc-attachment>...<figure>...</figure></bc-attachment>,</p>`).
 *
 * `<figure>` is flow (block-level) content, which is not permitted inside `<p>`
 * per the HTML5 content model. Per the standard HTML parsing algorithm, any
 * compliant browser parser (including `DOMParser`/`innerHTML`, used by both the
 * browser AND `dangerouslySetInnerHTML`) auto-closes the open `<p>` (and pops
 * the still-open `<bc-attachment>` with it) the instant it sees the nested
 * `<figure>` start tag - *before* the figure's own children are attached. The
 * net effect: the figure (avatar + name) is silently relocated to become a
 * sibling of the original `<p>` instead of a descendant of the attachment, and
 * any text that followed the mention in the source (e.g. a trailing comma)
 * becomes an orphaned text node dropped in between. That produces the visible
 * "Hi" / [avatar image] / "Kenneth" / "," split across four lines, and it also
 * silently breaks any CSS written against `bc-attachment ... figure` selectors,
 * since that ancestor relationship no longer exists once the browser has
 * "corrected" the markup.
 *
 * The fix: parse once with `DOMParser` (letting the browser apply its normal,
 * unavoidable auto-close correction), then walk the *resulting* tree to find
 * mention attachments - whether or not they got displaced - and rebuild each
 * one as flat, inline-safe markup (`<span><img>Name</span>`) with no block
 * tags, re-attaching any stray trailing text. Re-serializing that is then safe
 * to set via `dangerouslySetInnerHTML`: there is no more `<figure>`/`<figcaption>`
 * for the parser to object to, so no further correction happens and the mention
 * flows inline with the surrounding text exactly like Basecamp's own rendering.
 */

const BLOCK_LEVEL_TAGS = new Set([
  'P', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'FIGURE', 'DIV',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE',
]);

function isBlockLevelElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE && BLOCK_LEVEL_TAGS.has((node as Element).tagName);
}

/**
 * Finds the `<figure>` associated with a mention `bc-attachment`, whether it
 * is still nested inside the attachment (uncorrupted) or was displaced to be
 * a sibling of the attachment's host `<p>` (corrupted by parser auto-close).
 */
function findMentionFigure(attachment: Element): Element | null {
  const nested = attachment.querySelector('figure');
  if (nested) return nested;

  const hostParagraph = attachment.closest('p');
  if (!hostParagraph) return null;

  let node: ChildNode | null = hostParagraph.nextSibling;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      return (node as Element).tagName === 'FIGURE' ? (node as Element) : null;
    }
    node = node.nextSibling;
  }
  return null;
}

/** Collects stray inline siblings after a displaced figure, up to the next block element. */
function collectStrayInlineSiblings(figure: Element): ChildNode[] {
  const stray: ChildNode[] = [];
  let node: ChildNode | null = figure.nextSibling;
  while (node && !isBlockLevelElement(node)) {
    stray.push(node);
    node = node.nextSibling;
  }
  return stray;
}

const BASECAMP_BLOB_HOSTS = new Set([
  'preview.app.basecamp.com',
  'storage.app.basecamp.com',
  'preview.3.basecamp.com',
  'storage.3.basecamp.com',
  '3.basecampapi.com',
])

export function isBasecampBlobUrl(raw: string | null | undefined): boolean {
  if (!raw) return false
  try {
    const parsed = new URL(raw)
    if (parsed.protocol !== 'https:' || !BASECAMP_BLOB_HOSTS.has(parsed.hostname)) {
      return false
    }
    if (parsed.hostname === '3.basecampapi.com') {
      return /\/blobs\/[0-9a-f-]{36}\//i.test(parsed.pathname)
    }
    return true
  } catch {
    return false
  }
}

function firstUrlFromSrcset(srcset: string): string {
  const first = srcset.split(',')[0]?.trim()
  return first ? first.split(/\s+/)[0] || '' : ''
}

function imageUrlFromAttachment(attachment: Element): string {
  const href = attachment.getAttribute('href') || ''
  const url = attachment.getAttribute('url') || ''
  // Prefer href (storage/download, includes filename) over preview url.
  const fromAttr = isBasecampBlobUrl(href) ? href : url
  if (isBasecampBlobUrl(fromAttr)) return fromAttr
  const img = attachment.querySelector('img')
  const src = img?.getAttribute('src') || ''
  if (isBasecampBlobUrl(src)) return src
  const fromSrcset = firstUrlFromSrcset(img?.getAttribute('srcset') || '')
  if (isBasecampBlobUrl(fromSrcset)) return fromSrcset
  return fromAttr || src || fromSrcset
}

function findImageFigure(attachment: Element): Element | null {
  return attachment.querySelector('figure') || findMentionFigure(attachment)
}

function rewriteImageAttachments(doc: Document): void {
  const attachments = Array.from(doc.body.querySelectorAll('bc-attachment'))
  for (const attachment of attachments) {
    const type = (attachment.getAttribute('content-type') || '').toLowerCase()
    if (type === 'application/vnd.basecamp.mention') continue
    if (type && !type.startsWith('image/')) continue

    const remote = imageUrlFromAttachment(attachment)
    if (!isBasecampBlobUrl(remote)) continue

    const figure = findImageFigure(attachment)
    const filename =
      attachment.getAttribute('filename') ||
      figure?.querySelector('figcaption')?.textContent?.trim() ||
      'image'

    const out = doc.createElement('figure')
    out.className = 'bc-image-attachment'

    const img = doc.createElement('img')
    img.setAttribute('alt', filename)
    img.setAttribute('data-bc-image-url', remote)
    img.setAttribute('loading', 'lazy')
    out.appendChild(img)

    const caption = doc.createElement('figcaption')
    caption.textContent = filename
    out.appendChild(caption)

    attachment.replaceWith(out)
    if (figure && figure.parentNode && figure !== out) figure.remove()
  }

  doc.body.querySelectorAll('img').forEach((img) => {
    if (img.classList.contains('bc-mention-avatar')) return
    if (img.getAttribute('data-bc-image-url')) return
    const src = img.getAttribute('src') || ''
    const fromSrcset = firstUrlFromSrcset(img.getAttribute('srcset') || '')
    const remote = isBasecampBlobUrl(src) ? src : fromSrcset
    if (!isBasecampBlobUrl(remote)) return
    img.removeAttribute('src')
    img.removeAttribute('srcset')
    img.setAttribute('data-bc-image-url', remote)
    if (!img.closest('figure.bc-image-attachment')) {
      const wrap = doc.createElement('figure')
      wrap.className = 'bc-image-attachment'
      img.replaceWith(wrap)
      wrap.appendChild(img)
    }
  })
}

function fixMentionAttachments(doc: Document): void {
  const attachments = Array.from(
    doc.body.querySelectorAll('bc-attachment[content-type="application/vnd.basecamp.mention"]')
  );

  for (const attachment of attachments) {
    const figure = findMentionFigure(attachment);
    if (!figure) continue;

    const strayNodes = attachment.contains(figure) ? [] : collectStrayInlineSiblings(figure);

    const img = figure.querySelector('img');
    const caption = figure.querySelector('figcaption');
    const name = caption?.textContent?.trim() ?? '';

    const inlineSpan = doc.createElement('span');
    inlineSpan.className = 'bc-mention-inline';
    if (img) {
      const avatar = doc.createElement('img');
      avatar.setAttribute('src', img.getAttribute('src') ?? '');
      avatar.setAttribute('alt', img.getAttribute('alt') || name);
      avatar.className = 'bc-mention-avatar';
      inlineSpan.appendChild(avatar);
    }
    if (name) {
      inlineSpan.appendChild(doc.createTextNode(name));
    }

    attachment.replaceWith(inlineSpan);

    if (strayNodes.length > 0) {
      const fragment = doc.createDocumentFragment();
      strayNodes.forEach((node) => fragment.appendChild(node));
      inlineSpan.after(fragment);
    }

    figure.remove();
  }
}

function significantChildren(el: Element): ChildNode[] {
  return Array.from(el.childNodes).filter(
    (node) => !(node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
  );
}

/** True when every child of `p` is a text node containing only whitespace (or there are no
 * children at all) - i.e. the tag carries no content whatsoever, not even a `<br>` spacer. */
function isFullyEmptyParagraph(p: Element): boolean {
  return significantChildren(p).length === 0;
}

/** Removes fully empty `<p>` elements - parser auto-close artifacts, never intentional content. */
function removeEmptyParagraphArtifacts(doc: Document): void {
  doc.body.querySelectorAll('p').forEach((p) => {
    if (isFullyEmptyParagraph(p)) p.remove();
  });
}

/**
 * Basecamp's editor stores a blank line as its own `<p><br></p>` (sometimes a `<div><br></div>`).
 * That `<br>` already occupies a full line, and paragraph margins on the blocks around it add
 * another line, so the rendered gap is about twice the break the author typed. Replace those
 * blocks with an empty `.bc-spacer` whose height is the whole gap.
 */
function collapseBlankLineSpacers(doc: Document): void {
  const blocks = Array.from(doc.body.querySelectorAll('p, div'));
  for (const block of blocks) {
    const children = significantChildren(block);
    const onlyBreaks =
      children.length > 0 &&
      children.every(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR'
      );
    if (!onlyBreaks) continue;
    block.classList.add('bc-spacer');
    block.replaceChildren();
  }
}

/**
 * Repairs Basecamp rich-text HTML so it is safe to render with
 * `dangerouslySetInnerHTML` and flows the same way Basecamp itself renders it.
 * Safe to call on any comment/description HTML - a no-op when there is
 * nothing to fix.
 */
export function fixBasecampCommentHtml(rawHtml: string): string {
  if (!rawHtml || typeof DOMParser === 'undefined') return rawHtml;

  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  fixMentionAttachments(doc);
  rewriteImageAttachments(doc);
  removeEmptyParagraphArtifacts(doc);
  collapseBlankLineSpacers(doc);
  return doc.body.innerHTML;
}
